import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceSummary } from '../devices/deviceModel';
import {
  disableGeofencePresence,
  enableGeofencePresence,
  readGeofenceConfig,
  watchGeofencePresence,
} from './geofencePresence';

function currentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('GEOLOCATION_UNAVAILABLE')); return; }
    navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: false, timeout: 20_000, maximumAge: 30_000 });
  });
}

export function GeofencePresenceControl({ devices, disabled = false }: { devices: DeviceSummary[]; disabled?: boolean }) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(() => readGeofenceConfig() !== null);
  const [working, setWorking] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    return watchGeofencePresence((message) => setStatus(t('homeAway.geofenceWatchFailed', { error: message })));
  }, [enabled, t]);

  const toggle = useCallback(async () => {
    if (working) return;
    setWorking(true); setStatus(null);
    try {
      if (enabled) {
        await disableGeofencePresence();
        setEnabled(false);
        setStatus(t('homeAway.geofenceDisabledDone'));
      } else {
        const pos = await currentPosition();
        await enableGeofencePresence(devices, pos.coords.latitude, pos.coords.longitude);
        setEnabled(true);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setStatus(message === 'NO_DETECTION_DEVICES'
        ? t('homeAway.noDetectionDevices')
        : t('homeAway.geofenceSetupFailed', { error: message }));
    } finally { setWorking(false); }
  }, [devices, enabled, t, working]);

  // 三平台對齊（2026-08-20，parity #8）：iOS 早已是 `Toggle`，web/Android 還停在
  // 「文字會變的按鈕」（「使用目前位置」/「停用」）。控制項的形狀不一致 → 同一個功能
  // 三份截圖對不上，而且按鈕型要靠讀文字反推現在是開還是關。
  // 這裡換成 `role="switch"`：開關本身就是狀態，副標只講**目前**怎麼樣。
  // ⚠️ `data-testid` 維持 `geofence-presence-toggle`（既有測試靠它）。
  const subtitle = working
    ? t('homeAway.geofenceSettingUp')
    : enabled
      ? t('homeAway.geofenceEnabledDesc')
      : t('homeAway.geofenceDisabledDesc');
  return (
    <div className="home-geofence">
      <div>
        <strong id="geofence-presence-label">{t('homeAway.geofenceTitle')}</strong>
        <p className="text-secondary home-geofence-desc">{subtitle}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-labelledby="geofence-presence-label"
        className={`home-switch${enabled ? ' home-switch-on' : ''}`}
        disabled={disabled || working}
        onClick={() => void toggle()}
        data-testid="geofence-presence-toggle"
      >
        <span className="home-switch-thumb" aria-hidden />
      </button>
      {status ? <p className="home-mode-status" role="status">{status}</p> : null}
    </div>
  );
}
